import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Moment } from 'moment'
import moment from 'moment'
import { useIntl } from 'react-intl'

import type { TypeaheadTravelDates } from '@luxuryescapes/contract-search'
import { DATE_SEARCH_OPTION_IDS, DEFAULT_FLEXIBLE_DURATION_RANGE, type FLEXIBLE_DURATION_RANGE } from 'constants/search'
import {
  INTL_DAY_MONTH_NAME_SHORT,
  INTL_DMY_CASUAL_SHORT_FORMAT,
} from 'constants/dateFormats'
import {
  addDays,
  addMonths,
  beginningOfToday,
  useIntlDateFormatter,
} from 'lib/datetime/dateUtils'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import { convertDateRangesToMonthString, mapFlexibleNightsToDurationRange } from 'lib/search/searchUtils'

interface UseCarouselDateFilterArgs {
  anytimeLabel: string
  initialDates?: TypeaheadTravelDates
}

function deriveInitialDateState(dates: TypeaheadTravelDates | undefined): {
  dateMode: DATE_SEARCH_OPTION_IDS
  checkIn: Moment | undefined
  checkOut: Moment | undefined
  flexibleMonths: string
  flexibleNights: FLEXIBLE_DURATION_RANGE | undefined
} {
  if (!dates || dates.type === 'anytime') {
    return { dateMode: DATE_SEARCH_OPTION_IDS.ANYTIME, checkIn: undefined, checkOut: undefined, flexibleMonths: '', flexibleNights: undefined }
  }

  if (dates.type === 'specific') {
    return {
      dateMode: DATE_SEARCH_OPTION_IDS.SPECIFIC,
      checkIn: moment(dates.checkIn),
      checkOut: moment(dates.checkOut),
      flexibleMonths: '',
      flexibleNights: undefined,
    }
  }

  if (dates.type === 'flexible') {
    const months = convertDateRangesToMonthString(dates.dateRanges) ?? ''
    const nightsRange = mapFlexibleNightsToDurationRange(dates.numberOfNights)

    return {
      dateMode: DATE_SEARCH_OPTION_IDS.FLEXIBLE,
      checkIn: undefined,
      checkOut: undefined,
      flexibleMonths: months,
      flexibleNights: nightsRange,
    }
  }

  return { dateMode: DATE_SEARCH_OPTION_IDS.ANYTIME, checkIn: undefined, checkOut: undefined, flexibleMonths: '', flexibleNights: undefined }
}

export default function useCarouselDateFilter({ anytimeLabel, initialDates }: UseCarouselDateFilterArgs) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const today = useMemo(() => beginningOfToday(), [])

  const [initial] = useState(() => deriveInitialDateState(initialDates))

  const [dateMode, setDateMode] = useState<DATE_SEARCH_OPTION_IDS>(initial.dateMode)
  const [checkIn, setCheckIn] = useState<Moment | undefined>(initial.checkIn)
  const [checkOut, setCheckOut] = useState<Moment | undefined>(initial.checkOut)
  const [flexibleMonths, setFlexibleMonths] = useState<string>(initial.flexibleMonths)
  const [flexibleNights, setFlexibleNights] = useState<FLEXIBLE_DURATION_RANGE | undefined>(initial.flexibleNights)

  const [hasUserEdited, setHasUserEdited] = useState(false)

  useEffect(() => {
    if (hasUserEdited) return
    const derived = deriveInitialDateState(initialDates)
    setDateMode(derived.dateMode)
    setCheckIn(derived.checkIn)
    setCheckOut(derived.checkOut)
    setFlexibleMonths(derived.flexibleMonths)
    setFlexibleNights(derived.flexibleNights)
  }, [hasUserEdited, initialDates])

  const [open, setOpen] = useState(false)
  const [pendingDateMode, setPendingDateMode] = useState<DATE_SEARCH_OPTION_IDS>(initial.dateMode)
  const [pendingCheckIn, setPendingCheckIn] = useState<Moment | undefined>(initial.checkIn)
  const [pendingCheckOut, setPendingCheckOut] = useState<Moment | undefined>(initial.checkOut)
  const [pendingFlexibleMonths, setPendingFlexibleMonths] = useState<string>(initial.flexibleMonths)
  const [pendingFlexibleNights, setPendingFlexibleNights] = useState<FLEXIBLE_DURATION_RANGE | undefined>(initial.flexibleNights)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const chipLabel = useMemo(() => {
    if (dateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC && checkIn && checkOut) {
      const startFormat = checkIn.year() !== checkOut.year() ?
        INTL_DMY_CASUAL_SHORT_FORMAT :
        INTL_DAY_MONTH_NAME_SHORT
      const endFormat = checkIn.year() !== checkOut.year() ?
        INTL_DMY_CASUAL_SHORT_FORMAT :
        INTL_DAY_MONTH_NAME_SHORT

      return `${dateFormatter(checkIn, startFormat)} - ${dateFormatter(checkOut, endFormat)}`
    }

    if (dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE && (flexibleMonths || flexibleNights)) {
      return prettyFlexibleDates(flexibleMonths, flexibleNights, undefined, intl)
    }

    return anytimeLabel
  }, [anytimeLabel, checkIn, checkOut, dateMode, flexibleMonths, flexibleNights, intl, dateFormatter])

  const filterNights = useMemo<number | undefined>(() => {
    if (dateMode !== DATE_SEARCH_OPTION_IDS.SPECIFIC || !checkIn || !checkOut) return undefined
    const nights = checkOut.diff(checkIn, 'days')
    return nights > 0 ? nights : undefined
  }, [checkIn, checkOut, dateMode])

  const handleToggle = useCallback(() => {
    if (!open) {
      setPendingDateMode(dateMode)
      setPendingCheckIn(checkIn)
      setPendingCheckOut(checkOut)
      setPendingFlexibleMonths(flexibleMonths)
      setPendingFlexibleNights(flexibleNights)
    }
    setOpen(prev => !prev)
  }, [checkIn, checkOut, open, dateMode, flexibleMonths, flexibleNights])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handleSelectSpecific = useCallback(() => {
    setPendingDateMode(DATE_SEARCH_OPTION_IDS.SPECIFIC)
    setPendingFlexibleMonths('')
    setPendingFlexibleNights(undefined)
  }, [])

  const handleSelectFlexible = useCallback(() => {
    setPendingDateMode(DATE_SEARCH_OPTION_IDS.FLEXIBLE)
    setPendingCheckIn(undefined)
    setPendingCheckOut(undefined)
  }, [])

  const handleSelectAnytime = useCallback(() => {
    setPendingDateMode(DATE_SEARCH_OPTION_IDS.ANYTIME)
    setPendingCheckIn(undefined)
    setPendingCheckOut(undefined)
    setPendingFlexibleMonths('')
    setPendingFlexibleNights(undefined)
  }, [])

  const handlePendingDatesChange = useCallback((dates: { startDate: Moment; endDate?: Moment | null }) => {
    setPendingCheckIn(dates.startDate)
    setPendingCheckOut(dates.endDate ?? undefined)
    if (dates.startDate && dates.endDate) {
      setPendingDateMode(DATE_SEARCH_OPTION_IDS.SPECIFIC)
    }
  }, [])

  const handlePendingFlexibleNightsChange = useCallback((duration: FLEXIBLE_DURATION_RANGE) => {
    setPendingFlexibleNights(duration)
    setPendingDateMode(DATE_SEARCH_OPTION_IDS.FLEXIBLE)
  }, [])

  const handlePendingFlexibleMonthsChange = useCallback((months: string) => {
    setPendingFlexibleMonths(months)
    if (months.length > 0) {
      setPendingDateMode(DATE_SEARCH_OPTION_IDS.FLEXIBLE)
      setPendingFlexibleNights(prev => prev ?? DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION)
    }
  }, [])

  const handleApply = useCallback(() => {
    setHasUserEdited(true)
    setDateMode(pendingDateMode)
    setCheckIn(pendingCheckIn)
    setCheckOut(pendingCheckOut)
    setFlexibleMonths(pendingFlexibleMonths)
    setFlexibleNights(pendingFlexibleNights)
    setOpen(false)
  }, [pendingCheckIn, pendingCheckOut, pendingDateMode, pendingFlexibleMonths, pendingFlexibleNights])

  const clear = useCallback(() => {
    setHasUserEdited(true)
    setDateMode(DATE_SEARCH_OPTION_IDS.ANYTIME)
    setCheckIn(undefined)
    setCheckOut(undefined)
    setFlexibleMonths('')
    setFlexibleNights(undefined)
  }, [])

  const calendarMaxDate = useMemo(
    () => pendingCheckIn && !pendingCheckOut ? addDays(pendingCheckIn.toDate(), 31) : addMonths(today, 36),
    [pendingCheckIn, pendingCheckOut, today],
  )

  const shouldShowSpecificPicker = pendingDateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC ||
    pendingDateMode === DATE_SEARCH_OPTION_IDS.ANYTIME
  const shouldShowFlexiblePicker = pendingDateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE
  const isApplyDisabled = pendingDateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC &&
    !(pendingCheckIn && pendingCheckOut)

  return {
    dateMode,
    checkIn,
    checkOut,
    flexibleMonths,
    flexibleNights,
    chipLabel,
    filterNights,
    open,
    hasUserEdited,
    pendingDateMode,
    pendingCheckIn,
    pendingCheckOut,
    pendingFlexibleMonths,
    pendingFlexibleNights,
    triggerRef,
    today,
    calendarMaxDate,
    shouldShowSpecificPicker,
    shouldShowFlexiblePicker,
    isApplyDisabled,
    handleToggle,
    handleClose,
    handleSelectSpecific,
    handleSelectFlexible,
    handleSelectAnytime,
    handlePendingDatesChange,
    handlePendingFlexibleNightsChange,
    handlePendingFlexibleMonthsChange,
    handleApply,
    clear,
  }
}
